import { Injectable, signal, inject } from '@angular/core';
import { UserService } from './user.service';

/**
 * Tracks recently-opened projects and pinned projects for the sidebar.
 * Persisted to the user's account (UserSettings.dashboardSettings) so it survives
 * across sessions and devices, with localStorage as an instant local cache.
 */
@Injectable({ providedIn: 'root' })
export class RecentProjectsService {
  private userService = inject(UserService);

  private readonly RECENT_KEY = 'sel.recentProjects';
  private readonly PINNED_KEY = 'sel.pinnedProjects';
  private readonly MAX_RECENT = 8;

  recent = signal<string[]>(this.load(this.RECENT_KEY));
  pinned = signal<string[]>(this.load(this.PINNED_KEY));

  private hydrated = false;
  private latestDashboardSettings: any = null;
  private saveTimer: any = null;

  constructor() {
    // Hydrate from (and sync with) the user's saved settings.
    this.userService.settings$.subscribe(settings => {
      if (!settings) return;
      this.latestDashboardSettings = (settings as any).dashboardSettings ?? {};
      if (this.hydrated) return;
      this.hydrated = true;

      const stored = this.latestDashboardSettings?.projects;
      if (stored && (Array.isArray(stored.pinned) || Array.isArray(stored.recent))) {
        // Account is the source of truth once loaded.
        if (Array.isArray(stored.pinned)) { this.pinned.set(stored.pinned); this.save(this.PINNED_KEY, stored.pinned); }
        if (Array.isArray(stored.recent)) { this.recent.set(stored.recent); this.save(this.RECENT_KEY, stored.recent); }
      } else if (this.pinned().length || this.recent().length) {
        // Nothing saved yet — push the local cache up.
        this.persist();
      }
    });
  }

  private load(key: string): string[] {
    try {
      const raw = localStorage.getItem(key);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter(v => typeof v === 'string') : [];
    } catch {
      return [];
    }
  }

  private save(key: string, val: string[]) {
    try {
      localStorage.setItem(key, JSON.stringify(val));
    } catch {
      /* storage unavailable — ignore */
    }
  }

  /** Debounced write of pins/recents to the user's account settings. */
  private persist() {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      const dashboardSettings = {
        ...(this.latestDashboardSettings ?? {}),
        projects: { pinned: this.pinned(), recent: this.recent() },
      };
      this.userService.updateUserSettings({ dashboardSettings }).catch(e =>
        console.error('[RecentProjects] Failed to save to settings', e),
      );
    }, 600);
  }

  /** Move a project to the front of the recent list (deduped, capped). */
  recordOpen(projectId: string) {
    if (!projectId) return;
    const next = [projectId, ...this.recent().filter(id => id !== projectId)].slice(0, this.MAX_RECENT);
    this.recent.set(next);
    this.save(this.RECENT_KEY, next);
    this.persist();
  }

  isPinned(id: string): boolean {
    return this.pinned().includes(id);
  }

  togglePin(projectId: string) {
    const cur = this.pinned();
    const next = cur.includes(projectId) ? cur.filter(id => id !== projectId) : [...cur, projectId];
    this.pinned.set(next);
    this.save(this.PINNED_KEY, next);
    this.persist();
  }
}
